// Session store of the matched-buyers response per (realtor, listing) — the ONE place the listing
// tab, the batch dialog and anything else that computes matches read and write.
//
// Why it exists. Reported 2026-09-17/18: "every time I open the listing it loads the results and
// they are not available; the התאמה details are missing". Three causes, all fixed here:
//   1. The tab rebuilt its rows from the Firestore `listing-matches` doc. That doc never carries the
//      reasons (its biggest docs are ~550KB of a 1MB cap) nor the colleagues' groups, and for most
//      listings it is written by the neighbourhood-only NOTIFICATION flow — a different population.
//      Reason-less, wrong-count rows that looked final. The tab no longer reads that doc at all.
//   2. The full response lived only in a per-component state + a per-instance in-flight ref, so
//      closing/reopening (or switching dialog tabs) mid-fetch started a SECOND ~25s compute and the
//      remounted tab never received the first one. In-flight dedupe is now MODULE-level: every
//      caller for a key gets the same promise, and a mounted tab learns about the result through
//      `subscribeMatchesSession` whoever started it.
//   3. A reload/new tab had nothing to show for the whole compute. `loadSnapshotMatches` reads the
//      server's LAST computed result (`…/:listingId/last`, reasons and office rows included) in one
//      read, while the live compute runs.
//
// Rules the store enforces:
//   - `source: "snapshot"` never overwrites `source: "live"` (a late snapshot is older by
//     definition); a live answer always replaces a snapshot.
//   - A snapshot's own rows are a PROJECTION of the contact — the tab marks them provisional and
//     the table withholds bulk SENDS until the live answer lands.
//   - A failed load rejects to every awaiter and leaves the previous entry intact.
//   - `undefined` means NOT LOADED. Never synthesize an empty list for data that was not fetched —
//     `fromOrgContacts: []` reads as "known empty" and hid the office count once already.
import type { REST_schema } from "../../../../@types/sharedSchema";

export type MatchesEntrySource = "live" | "snapshot";

export type MatchesSessionEntry = {
  data: REST_schema.RelevantContactsForListingResponse;
  source: MatchesEntrySource;
  /** When THIS client received it (ms epoch). Drives the "fresh enough, don't refetch" check. */
  fetchedAt: number;
  /**
   * When the SERVER computed it (ms epoch): `fetchedAt` for a live answer, the snapshot's own
   * `computedAt` for a snapshot. `NaN` when a snapshot did not say — shown as "not yet computed",
   * never as "just now".
   */
  computedAt: number;
  /** The snapshot hit the server's size cap and holds only the first rows. */
  truncated?: boolean;
};

type Listener = () => void;

/** Opening one listing after another adds an entry each; a few hundred responses is plenty. */
export const MAX_MATCHES_SESSION_ENTRIES = 150;

const store = new Map<string, MatchesSessionEntry>();
const listeners = new Map<string, Set<Listener>>();
const liveInFlight = new Map<string, Promise<MatchesSessionEntry>>();
const snapshotInFlight = new Map<string, Promise<MatchesSessionEntry | undefined>>();
/** Bumped by `clearMatchesSession` so a load that started before the clear cannot write after it. */
let generation = 0;

export const matchesSessionKey = (userEmail: string | undefined | null, listingId: string): string =>
  `${(userEmail ?? "").toLowerCase()}|${listingId}`;

function notify(key: string): void {
  const keyListeners = listeners.get(key);
  if (!keyListeners) return;
  // Copied: a listener may unsubscribe itself (or another) while we iterate.
  Array.from(keyListeners).forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error("matchesSessionCache: a listener threw", error);
    }
  });
}

/** The entry for `key`, if any. Counts as a use for the LRU order. */
export function readMatchesSession(key: string): MatchesSessionEntry | undefined {
  const entry = store.get(key);
  if (entry) {
    // LRU touch: re-insert so the entry moves to the end of the Map's order.
    store.delete(key);
    store.set(key, entry);
  }
  return entry;
}

/**
 * Stores `entry` under `key` (LRU, capped) and notifies that key's subscribers. Unconditional — the
 * snapshot-vs-live rule lives in `loadSnapshotMatches`.
 */
export function writeMatchesSession(key: string, entry: MatchesSessionEntry): void {
  store.delete(key);
  store.set(key, entry);
  evictOverflow();
  notify(key);
}

/** Oldest first, skipping keys a mounted component is watching (its rows must not vanish under it). */
function evictOverflow(): void {
  if (store.size <= MAX_MATCHES_SESSION_ENTRIES) return;
  for (const key of Array.from(store.keys())) {
    if (store.size <= MAX_MATCHES_SESSION_ENTRIES) return;
    if (!listeners.get(key)?.size) store.delete(key);
  }
}

/** `listener` runs after every write to `key` and whenever its live-load state changes. */
export function subscribeMatchesSession(key: string, listener: Listener): () => void {
  let keyListeners = listeners.get(key);
  if (!keyListeners) {
    keyListeners = new Set();
    listeners.set(key, keyListeners);
  }
  keyListeners.add(listener);
  return () => {
    const current = listeners.get(key);
    if (!current) return;
    current.delete(listener);
    if (current.size === 0) listeners.delete(key);
  };
}

export function isLiveInFlight(key: string): boolean {
  return liveInFlight.has(key);
}

/**
 * Computes the matches live, deduped per key across the whole app: a second caller while one is in
 * flight gets the SAME promise (no second ~25s compute). On success the answer is written as
 * `live`; on failure every awaiter gets the rejection and the previous entry stays as it was.
 * Subscribers are notified when the load starts and when it settles (`isLiveInFlight`).
 */
export function loadLiveMatches(
  key: string,
  fetcher: () => Promise<REST_schema.RelevantContactsForListingResponse>
): Promise<MatchesSessionEntry> {
  const existing = liveInFlight.get(key);
  if (existing) return existing;

  const startedIn = generation;
  // `.then(fetcher)`, not `fetcher()`: the promise must be registered below before the fetcher can
  // settle (or throw synchronously), or its cleanup would run before it is registered.
  const promise = Promise.resolve()
    .then(fetcher)
    .then(
      (data) => {
        const now = Date.now();
        const entry: MatchesSessionEntry = { data, source: "live", fetchedAt: now, computedAt: now };
        if (startedIn !== generation) return entry;
        // Out of flight BEFORE the write, so its single notification carries both changes.
        liveInFlight.delete(key);
        writeMatchesSession(key, entry);
        return entry;
      },
      (error: unknown) => {
        if (startedIn === generation) {
          liveInFlight.delete(key);
          notify(key);
        }
        throw error;
      }
    );
  liveInFlight.set(key, promise);
  notify(key);
  return promise;
}

/**
 * Reads the server's last computed result, deduped per key. Written as `snapshot` only when the key
 * holds no `live` entry at the moment it lands; a `null` snapshot writes nothing. Resolves to the
 * entry it wrote, or `undefined` when it wrote nothing.
 */
export function loadSnapshotMatches(
  key: string,
  fetcher: () => Promise<REST_schema.LastRelevantContactsForListingResponse>
): Promise<MatchesSessionEntry | undefined> {
  const existing = snapshotInFlight.get(key);
  if (existing) return existing;

  const startedIn = generation;
  const promise = Promise.resolve()
    .then(fetcher)
    .then(
      (response) => {
        if (startedIn !== generation) return undefined;
        snapshotInFlight.delete(key);
        if (!response?.snapshot) return undefined;
        if (store.get(key)?.source === "live") return undefined;
        const now = Date.now();
        const computedAt = response.computedAt ? Date.parse(response.computedAt) : Number.NaN;
        const entry: MatchesSessionEntry = {
          data: response.snapshot,
          source: "snapshot",
          fetchedAt: now,
          computedAt,
          ...(response.truncated ? { truncated: true } : {})
        };
        writeMatchesSession(key, entry);
        return entry;
      },
      (error: unknown) => {
        if (startedIn === generation) snapshotInFlight.delete(key);
        throw error;
      }
    );
  snapshotInFlight.set(key, promise);
  return promise;
}

/** Drops every entry and in-flight marker; loads already running will not write afterwards. */
export function clearMatchesSession(): void {
  generation += 1;
  store.clear();
  liveInFlight.clear();
  snapshotInFlight.clear();
  Array.from(listeners.keys()).forEach(notify);
}
